import { useEffect, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { db } from '../../../../config/firebase';

/**
 * Smart Template Engine — Preview System (Phase 3A).
 *
 * Reads the FULL RTDB wall node `${ENV}/VideoWalls/{wallId}` (what the backend
 * wrote on start) + `.info/serverTimeOffset`, so the frontend can compute "what's
 * showing now" exactly like the device — the wall player does NOT report its
 * current item, so we derive it from start_timestamp + per-item durations.
 *
 * Returns { state, startTimestamp, totalDurationMs, loop, rows, columns,
 *           orientation, playlist[], serverOffset, serverNow() }.
 * Read-only — same `db`/anon-auth pattern as useWallSyncTelemetry. ZERO sync contact.
 */
export default function useWallLiveState(wallId) {
  const [val, setVal] = useState(null);
  const [serverOffset, setServerOffset] = useState(0);

  useEffect(() => {
    if (!db) return undefined;
    const offRef = ref(db, '.info/serverTimeOffset');
    const unsub = onValue(offRef, (snap) => setServerOffset(snap.val() || 0));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!wallId || !db) return undefined;
    const node = ref(db, `${process.env.REACT_APP_APP_ENV}/VideoWalls/${wallId}`);
    const unsub = onValue(node, (snap) => setVal(snap.val() || null));
    return () => unsub();
  }, [wallId]);

  const playlistRaw = val?.playlist;
  const playlist = Array.isArray(playlistRaw)
    ? playlistRaw.filter(Boolean)
    : (playlistRaw ? Object.values(playlistRaw).filter(Boolean) : []);

  return {
    raw: val,
    state: val?.state || '',
    startTimestamp: Number(val?.start_timestamp) || 0,
    totalDurationMs: Number(val?.total_duration_ms) || 0,
    loop: val?.loop !== false,
    rows: Number(val?.rows) || 1,
    columns: Number(val?.columns) || 1,
    orientation: val?.orientation || 'landscape',
    playlist,
    // Per-screen telemetry incl. lastSync heartbeat — used to detect a device that
    // left wall mode but whose stale "playing" data still lingers (Bug 3).
    screens: val?.screens || {},
    serverOffset,
    serverNow: () => Date.now() + serverOffset,
  };
}
